import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import { useOperatingSystem } from '@studio/react-utils/user-agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, MouseEvent, ReactElement } from 'react';
import { twMerge } from 'cn';
import type { EditorProps } from './types.js';
import { toCombo, toString } from '#~/hot-keys/index.js';
import type { Combo } from '#~/hot-keys/index.js';

/** `<Editor>` component. */
export const Editor = ({
  className,
  focusOnMount,
  onBlur,
  onCancel,
  onChange,
  onClick,
  onCommit,
  onKeyDown,
  value = false,
  ...props
}: EditorProps): ReactElement => {
  const [interimValue, setInterimValue] = useState(value);
  const os = useOperatingSystem();

  const ref = useRef<HTMLDivElement>(null);

  const handleChange = useLatestFn((nextValue: T_Maybe<Combo | false> = false) => {
    onChange?.(nextValue);

    setInterimValue(nextValue);
  });

  const handleClick = useLatestFn((event: MouseEvent<HTMLDivElement>) => {
    onClick?.(event);

    if (!event.isDefaultPrevented()) {
      ref.current?.focus();
    }
  });

  const handleKeyDown = useLatestFn((event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);

    if (event.isDefaultPrevented()) {
      return;
    }

    const { key } = event;

    if (key === 'Tab') {
      // Let the event run its course.
      return;
    }

    event.preventDefault();

    if (key === 'Enter') {
      onCommit?.(interimValue);

      return;
    }

    if (key === 'Escape') {
      onCancel?.();

      return;
    }

    handleChange(['Backspace', 'Delete'].includes(key) ? false : toCombo(event));
  });

  const handleBlur = useLatestFn((event: FocusEvent<HTMLDivElement>) => {
    onBlur?.(event);

    if (event.isDefaultPrevented()) {
      return;
    }

    if (interimValue) {
      onCommit?.(interimValue);
    } else {
      onCancel?.();
    }
  });

  useMount(() => {
    if (focusOnMount) {
      ref.current?.focus();
    }
  });

  return (
    <div
      className={twMerge(
        'relative grid h-34 w-[125px] cursor-pointer place-items-center rounded border transition-colors focus:border-primary',
        className
      )}
      onBlur={handleBlur}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      ref={ref}
      role='textbox'
      tabIndex={0}
      {...props}
    >
      {interimValue ? (
        toString(os, interimValue)
      ) : (
        <div className='text-sm text-muted'>Type new hot key</div>
      )}
    </div>
  );
};
